import { MonitoringReportState } from '../shared/types';
import { ReportPhotoDTO } from '../shared/photos/types';
import {
  BudgetSummaryDTO,
  DrawSummary,
  ProjectLineItemDTO,
  ProjectLineItemSubtotalDTO,
  SubtotalReanchorDTO,
} from '../../project/project.type';

/**
 * Everything the report knows about one covered draw. `draws` on the report state is the single
 * source of truth for multi-draw content; the legacy top-level `drawSummary`/`budgetSummary`/
 * `lineItems` fields mirror whichever of these is the primary draw.
 */
export interface DrawContext {
  drawId: string;
  drawSummary: DrawSummary;
  budgetSummary?: BudgetSummaryDTO;
  lineItems?: ProjectLineItemDTO[];
  subtotals?: ProjectLineItemSubtotalDTO[];
  /** Subtotal re-anchors from Review Budget (deleted anchor rows, range edits), sent with the next budget save. */
  pendingSubtotalReanchors?: SubtotalReanchorDTO[];
}

export type MagicReportDTO = any; // Replace with real DTOs if available

/**
 * One row of the "Recap of Previous Applications" (one table per draw). Seeded from the Excel
 * Report tab and appended to on every report reset; independent of the draw's previous fundings.
 */
export interface RecapApplicationDTO {
  id: string;
  projectDrawId: string;
  /** Table title as printed on the Excel Report tab, e.g. "<Contractor>'s <G702 Name> Application:". */
  heading: string;
  applicationNumber?: string | null;
  applicationDate?: string | null;
  requestedAmount?: number | null;
  approvedAmount?: number | null;
  retainage?: number | null;
  percentRequested?: number | null;
  percentComplete?: number | null;
  contractAmount?: number | null;
  displayOrder: number;
}

export interface MagicReportInvoiceDTO {
  id: string;
  magicReportId: string;
  reportId?: string; // For generic usage
  invoice: {
    id: string;
    number: string;
    invoiceAmount: number;
    paidAmount: number;
    invoiceDate?: string;
    createdOn?: string;
    updatedOn?: string;
  };
  recepient?: string;
  recepientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  projectName?: string;
  projectAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  service?: string;
  currentInvoice?: boolean;
  createdOn?: string;
  updatedOn?: string;
}
export interface CreateMagicReportDTO {
  name: string;
  projectId: string;
  /** Project draw ids this report covers. On reset this is the new desired set. */
  drawIds?: string[];
  /**
   * Subset of `drawIds` whose Change Orders and Lien Waivers sections are hidden. Omit to let the
   * backend carry the replaced report's set forward; never every covered draw.
   */
  hiddenCoLienDrawIds?: string[];
  startsNewPeriod?: boolean;
}
export type UpdateMagicReportDTO = any;

export interface CreateMagicReportInvoiceDTO {
  magicReportId: string;
  invoice: {
    number: string;
    invoiceAmount: number;
    paidAmount: number;
    invoiceDate?: string;
    comment?: string;
    changeReason?: string;
  };
  recepient?: string;
  recepientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  projectName?: string;
  projectAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  service?: string;
  currentInvoice?: boolean;
}

export type UpdateMagicReportInvoiceDTO = any;
export type MagicReportPhotoDTO = ReportPhotoDTO;
export type MagicReportPhotoUploadResponse = {
  id?: string;
  photoId?: string;
  blobName?: string;
  originalBlobName?: string;
  pdfBlobName?: string;
  thumbnailBlobName?: string;
};

export interface InvoiceDataDTO {
  isCompleted?: boolean;
}

export type MagicReportState = MonitoringReportState<
  MagicReportPhotoDTO,
  {
    magicReportId?: string;
    sections?: MagicReportSectionsDTO;
    coverPage?: CoverPageDTO;
    reportName?: string;
    invoiceData?: InvoiceDataDTO;
    /**
     * Every draw this report covers, ordered by application number. Client-side live data — only
     * the draw ids are persisted on the report; the budgets and line items are refetched on open.
     *
     * NOTE: the inherited `drawSummary`, `budgetSummary` and `lineItems` fields are a
     * @deprecated mirror of the PRIMARY draw (the last entry here), kept so sections that have
     * not been migrated to multi-draw keep working. Remove them once every consumer reads `draws`.
     */
    draws?: DrawContext[];
    /**
     * Covered draws whose Change Orders and Lien Waivers blocks are suppressed. Chosen in the
     * draw-selection dialog at create/reset time; read through `co-lien-visibility.util` rather
     * than directly, so the covered-set and never-hide-everything clamps always apply.
     */
    hiddenCoLienDrawIds?: string[];
    /** True from Start Next Report until the first open syncs every section from the budget. */
    initialSyncPending?: boolean;
    /** Recap of Previous Applications for every draw in the project, in print order. Read-only. */
    recapApplications?: RecapApplicationDTO[];
  }
>;

/** The draw that drives sections not yet migrated to multi-draw: the latest one covered. */
export const getPrimaryDraw = (draws?: DrawContext[]): DrawContext | undefined =>
  draws && draws.length > 0 ? draws[draws.length - 1] : undefined;

/**
 * Orders draws the way the project page does: by application number ascending, so the newest
 * application is last and therefore primary.
 */
export const sortDrawsByApplicationNumber = <T extends { drawSummary?: DrawSummary }>(draws: T[]): T[] =>
  [...draws].sort((a, b) => parseInt(a.drawSummary?.applicationNumber || '0', 10) - parseInt(b.drawSummary?.applicationNumber || '0', 10));

// Section Field Types - Must match backend enum values (integers)
export enum FieldType {
  Boolean = 0,
  Select = 1,
  Multiselect = 2,
  Text = 3,
  TextArray = 4,
  Date = 5,
  Number = 6,
}

export interface FieldEntryDTO {
  value?: string;
  addToActionItems?: boolean;
  /**
   * Ordering key for Action Items display.
   * Set when a bookmark is toggled ON so we can preserve intra-section click order.
   */
  actionItemOrder?: number;
  order: number;
  isFromLastMonth?: boolean;
}

export interface SectionFieldDTO {
  fieldName: string;
  fieldType: FieldType;
  value?: string;
  addToActionItems?: boolean;
  /**
   * Ordering key for Action Items display.
   * Set when a bookmark is toggled ON so we can preserve intra-section click order.
   */
  actionItemOrder?: number;
  entries?: FieldEntryDTO[];
  lastMonth?: string; // Last month's value for this field
}

export interface SectionFieldCollection {
  fields: SectionFieldDTO[];
  insertAfterSectionId?: string; // Position of dynamic section: 'start', static section id, or undefined/'end'
  isCompleted?: boolean;
}

/**
 * One draw's copy of a draw-scoped section, as stored by the API (a JSON array, one per draw).
 * `drawId` is absent on legacy single-draw reports and is bound to the first draw on load.
 */
export interface DrawScopedSectionFieldCollection extends SectionFieldCollection {
  drawId?: string;
}

/**
 * Column definition for user-created tables in dynamic sections.
 *
 * - 'text': plain string values
 * - 'currency': numeric values formatted as currency
 * - 'numeric': numeric values without formatting
 * - 'date': ISO date string (yyyy-MM-dd), edited via DynamicDatePicker
 */
export type DynamicTableColumnType = 'text' | 'currency' | 'numeric' | 'date';

export interface DynamicTableColumnDef {
  id: string;
  header: string;
  type: DynamicTableColumnType;
}

/** Row data for dynamic section tables (column id -> value); _id is internal row key */
export interface DynamicTableRow {
  _id: string;
  [columnId: string]: string | number | undefined;
}

export interface ScheduleGraphRowDTO {
  id: string;
  applicationNumber?: string;
  applicationDate?: string;
  amountFunded?: number;
  percentComplete?: number;
  comment?: string;
  isLastMonth?: boolean;
  /**
   * True on the one row that was seeded from the current draw's period on first load. Marks it
   * so it's only ever inserted once — after that it's a normal, fully editable row like any other.
   */
  isCurrentPeriod?: boolean;
  /**
   * True on the one row that was seeded from the project's commencement date on first load. Marks
   * it so it's only ever inserted once — after that it's a normal, fully editable row like any
   * other (including being deletable, unlike the old always-there commencement marker).
   */
  isCommencement?: boolean;
}

export interface ScheduleGraphAxisDTO {
  startDate?: string;
  endDate?: string;
  endDateIsEstimated?: boolean;
  hideCommencement?: boolean;
}

export interface MagicReportSectionsDTO {
  id?: string;
  magicReportId?: string;
  previousFundings?: SectionFieldCollection;
  /** Draw-scoped: one entry per covered draw. */
  applicationComments?: DrawScopedSectionFieldCollection[];
  /** Draw-scoped: one entry per covered draw. */
  reductionComments?: DrawScopedSectionFieldCollection[];
  schedule?: SectionFieldCollection;
  notaryAndSignatures?: SectionFieldCollection;
  planCostReviewIssues?: SectionFieldCollection;
  foundationSurvey?: SectionFieldCollection;
  progressDescription?: SectionFieldCollection;
  projectDescription?: SectionFieldCollection;
  minorAdjustments?: SectionFieldCollection;
  docsReceived?: SectionFieldCollection;
  materialTestingReports?: SectionFieldCollection;
  constructionMeetingMinutes?: SectionFieldCollection;
  potentialChangeOrders?: SectionFieldCollection;
  retainage?: SectionFieldCollection;
  storedMaterials?: SectionFieldCollection;
  reallocations?: SectionFieldCollection;
  changeOrders?: SectionFieldCollection;
  lienWaivers?: SectionFieldCollection;
  dynamicSections?: SectionFieldCollection[];
  scheduleGraph?: ScheduleGraphRowDTO[];
  scheduleGraphAxis?: ScheduleGraphAxisDTO;
  createdBy?: any;
  updatedBy?: any;
  createdOn?: string;
  updatedOn?: string;
}

export interface UpdateMagicReportSectionsDTO {
  previousFundings?: SectionFieldCollection;
  /**
   * Draw-scoped. The API replaces these WHOLESALE, so always send every covered draw's entry —
   * a partial array silently drops the omitted draws' comments.
   */
  applicationComments?: DrawScopedSectionFieldCollection[];
  reductionComments?: DrawScopedSectionFieldCollection[];
  schedule?: SectionFieldCollection;
  notaryAndSignatures?: SectionFieldCollection;
  planCostReviewIssues?: SectionFieldCollection;
  foundationSurvey?: SectionFieldCollection;
  progressDescription?: SectionFieldCollection;
  projectDescription?: SectionFieldCollection;
  minorAdjustments?: SectionFieldCollection;
  docsReceived?: SectionFieldCollection;
  materialTestingReports?: SectionFieldCollection;
  constructionMeetingMinutes?: SectionFieldCollection;
  potentialChangeOrders?: SectionFieldCollection;
  retainage?: SectionFieldCollection;
  storedMaterials?: SectionFieldCollection;
  reallocations?: SectionFieldCollection;
  changeOrders?: SectionFieldCollection;
  lienWaivers?: SectionFieldCollection;
  dynamicSections?: SectionFieldCollection[];
  scheduleGraph?: ScheduleGraphRowDTO[];
  scheduleGraphAxis?: ScheduleGraphAxisDTO;
}

export interface CoverPageBudgetRowDTO {
  budgetName: string;
  requestedAmount: number;
  amountRecommendedForFunding: number;
  percentComplete: number | null;
  /**
   * True once the reviewer types a % Complete on this row. Draw-backed rows without it follow the
   * draw's live % Complete; with it, the typed value wins everywhere it is printed.
   */
  isPercentCompleteOverridden?: boolean;
  /**
   * Set on rows derived from a covered draw. Such rows print as
   * "$X for {contractor}'s {contractName} Application #{applicationNumber}" and keep their amounts
   * synced from that draw's budget. Rows without a drawId are user-added (e.g. "Soft Costs") and
   * print using `budgetName` alone.
   */
  drawId?: string;
  contractor?: string;
  contractName?: string;
  applicationNumber?: string;
}

export type CoverPageGraphType = 'standard' | 'reallocations' | 'none';

export interface CoverPageDTO {
  budgetRows: CoverPageBudgetRowDTO[];
  contingencyPercentageUsed?: number | null;
  isBalanceSufficient?: boolean | null;
  graphType?: CoverPageGraphType;
  coverPagePhotoIds?: string[];
  isCompleted?: boolean;
}

// Version history
export type MagicReportRevisionKind =
  | 'Baseline'
  | 'Edit'
  | 'Restore'
  | 'Revert'
  | 'RestoreChanges'
  | 'RunThisMonth'
  | 'Export'
  /** Budget data of the covered draws changed outside this report (project page, pay app, another report). */
  | 'External';

export interface MagicReportRevisionAuthor {
  id: string;
  name: string;
}

export interface MagicReportRevisionSummary {
  id: string;
  revisionNumber: number;
  kind: MagicReportRevisionKind;
  /** Main wizard step id the edits were made on; null for fixed points. */
  step?: string | null;
  name?: string | null;
  isClosed: boolean;
  /** Keys such as `report.coverLetter` or `sections.schedule` that differ from the previous version. */
  changedKeys: string[];
  createdOn?: string | null;
  lastAmendedOn: string;
  keepForever: boolean;
  restoredFromRevisionId?: string | null;
  author?: MagicReportRevisionAuthor | null;
}

export interface MagicReportRevisionSession {
  latestRevisionId: string;
  startedOn?: string | null;
  endedOn: string;
  author?: MagicReportRevisionAuthor | null;
  changedKeys: string[];
  /** Newest first. */
  revisions: MagicReportRevisionSummary[];
}

export interface MagicReportRevisionList {
  /** Newest first. */
  sessions: MagicReportRevisionSession[];
  currentRevisionId?: string | null;
}

export interface MagicReportRevisionChange {
  key: string;
  /** Raw stored text before / after (JSON for structured fields); null when empty. */
  before?: string | null;
  after?: string | null;
}

/** One covered draw's budget data as a version recorded it, shaped like the budget-and-line-items endpoint. */
export interface MagicReportRevisionDraw {
  drawId: string;
  projectDrawSummary: DrawSummary;
  budgetSummary?: BudgetSummaryDTO | null;
  lineItems: ProjectLineItemDTO[];
}

export interface MagicReportRevisionPreview {
  revision: MagicReportRevisionSummary;
  /** The report as it was then; invoices and recap applications are current. */
  report: MagicReportDTO;
  /** The covered draws' budget data as it was then; empty for versions from before budget versioning. */
  draws?: MagicReportRevisionDraw[];
  /** Budget keys (`budget.*`) come without before/after; their data is in `draws`. */
  changes: MagicReportRevisionChange[];
  /** The version these changes are compared against; null for the first version. */
  previousRevisionId?: string | null;
}

export interface MagicReportApplyRevisionResult {
  /** The "Restored" / "Changes undone" version recorded. */
  revision?: MagicReportRevisionSummary | null;
  conflicts: { key: string; revisionNumbers: number[] }[];
  /** Budget data that couldn't be put back, e.g. a line item that no longer exists. */
  warnings?: string[];
}
